import * as React from 'react';
import { useEffect, useState, type JSX } from 'react';
import { useCollapse } from 'react-collapsed';

import { ExpandableBody } from './components/expandable-body';
import { ExpandableHead } from './components/expandable-head';

export interface ExpandableRowProps {
  children: React.ReactElement<unknown>[];
  removeStyling?: boolean;
  expanded?: boolean;
  onToggleRow?: (isExpanded: boolean) => void;
  shouldToggleOnClick?: boolean;
  disabled?: boolean;
}

export function ExpandableRow({
  children,
  removeStyling = false,
  expanded = false,
  disabled = false,
  onToggleRow,
  shouldToggleOnClick = true,
}: ExpandableRowProps): JSX.Element {
  const [isExpanded, setIsExpanded] = useState(expanded);
  const { getCollapseProps, getToggleProps } = useCollapse({
    isExpanded,
    duration: 200,
  });

  useEffect(() => {
    setIsExpanded(expanded);
  }, [expanded]);

  const headProps = {
    getToggleProps: getToggleProps({
      onClick: () => {
        if (!shouldToggleOnClick) {
          onToggleRow?.(isExpanded);
          return;
        }

        setIsExpanded((oldExpanded) => !oldExpanded);
        onToggleRow?.(!isExpanded);
      },
      disabled,
    }),
    isExpanded,
  };

  const bodyProps = {
    getCollapseProps,
  };

  if (removeStyling) {
    return (
      <>
        {React.cloneElement(children[0], headProps)}
        {React.cloneElement(children[1], bodyProps)}
      </>
    );
  }

  return (
    <div className="bg-background shadow-cardShadow overflow-hidden rounded-xs border">
      {React.cloneElement(children[0], headProps)}
      {React.cloneElement(children[1], bodyProps)}
    </div>
  );
}

export { ExpandableBody, ExpandableHead };
